<template>
    <div class="m-top-20 panel panel-info">
        <div v-if="showModal" class="modalDialog is-active" role="dialog" aria-labelledby="modal-title" aria-describedby="modal-description" aria-hidden="true" @keyup.esc="modalClose">
            <div class="modal-contents">
                <div>
                    <a title="Close" class="close" @click="showModal=false">X</a>
                    <h3><slot id="modal-title" name="header">Alert</slot></h3>
                    <hr />
                </div>
                <div>
                    <slot id="modal-description" name="body">
                        <span @click="showModal=false" v-html="modalMessage"></span>.
                    </slot>
                    <hr />
                </div>
                <div class="right">
                    <button class="btn btn-primary" @click="showModal=false">Close</button>
                </div>
                <div class="clear"></div>
            </div>
        </div>
        <div class="panel-heading centered bold big ">
            <div class="col-xs-7 centered bigger">
                Planner
            </div>
            <div class="right" style="display: inline-flex;">
                <span class="p-right-5"><div v-if="loading" class="inline-loader"></div></span>
                <span class="btn btn-default m-right-5 bold" @click="exportToExcel">Save to CSV</span>
            </div>
            <div class="clear"></div>
        </div>
        <ag-grid
            :name="name"
            :ref="reference"
            :iteration="iteration"
            :width="width"
            :height="height"
            :columns="columnDefs"
            :row-data="rowData"
            :options="gridOptions"
            sidebar="filters"
            :filter-order="filterOrder"
            :classes="'ag-theme-balham ' + (loading ? 'opacity-25' : '')"
        />
    </div>
</template>

<script>
import 'regenerator-runtime/runtime';
import Axios from '../classes/Axios.js';
import DateField from './DateField.vue';
import AgGrid from './AgGrid.vue';
import {defineComponent} from 'vue';

export default defineComponent ({
    name: "Planner",

    emits: ['changed'],

    components: {
        'date-field': DateField,
        'ag-grid': AgGrid,
    },

    props: {
        entityId: {
            type: [String, Number],
            required: false,
        },
        adminAccess: {
            type: Boolean,
            required: false,
            default: false,
        },
        name: {
            type: String,
            required: false,
            default: 'roster',
        },
        reference: {
            type: String,
            required: false,
            default: 'roster',
        },
        classes: {
            type: [ String, Array ],
            required: false,
            default: 'form-control'
        },
    },

    data() {
        return {
            waiting: false, // whole-page spinner
            loading: false, // spinner
            iteration: 0,
            searchQueryDateStart: undefined,
            searchQueryDateEnd: undefined,
            locale: window.navigator.userLanguage || window.navigator.language || 'en-US', // 'fr-fr' // test stuff
            oasisApi: new Axios('/restv2'),
            link: '',
            showModal: false,
            modalMessage: '',
            showAllShows: false,

            // AG Grid
            rowData: [],
            columnDefs: undefined,
            sidebar: undefined,
            filterOrder: undefined,
            gridOptions: {
                singleClickEdit: false,
                undoRedoCellEditing: false,
                enterMovesDown: true,
                enterMovesDownAfterEdit: true,
                rowSelection: 'multiple',
                suppressContextMenu: true,
                defaultColDef: {
                    resizable: true,
                    sortable: true,
                    menuTabs: [],
                    suppressSizeToFit: false,
                    headerCheckboxSelectionFilteredOnly: true,
                    filterParams: {
                        suppressMiniFilter: true,
                        defaultToNothingSelected: true,
                        buttons: ['reset', 'apply'],
                        debounceMs: 200
                    }
                },
                onGridReady: (event) => this.$refs[this.reference].onGridReady(event),
            }
        }
    },

    computed: {
        width() {
            // Reducing by 3 % to handle sigma-frontend navbar
            return '100%';
        },

        height() {
            // Reducing by 3 % to handle sigma-frontend navbar
            return window.innerHeight * 0.63 + 'px';
        },

        today() {
            let date = new Date();
            var day = date.getDate();
            var monthIndex = date.getMonth();
            var year = date.getFullYear();

            return year + '-' + this.pad(monthIndex * 1 + 1) + '-' + this.pad(day);
        },
    },

    methods: {
        createDialog(message) {
            this.modalMessage = message;
            this.showModal = true;
        },

        async getPlanner() {
            this.loading = true;
            this.rowData = undefined;
            const endpoint = '/getStarts/allStarts'
            let query = {};

            if (this.searchQueryDateStart) query.date__ge = new Date(this.searchQueryDateStart).toISOString().split('T')[0];
            if (this.searchQueryDateEnd) query.date__le = new Date(this.searchQueryDateEnd).toISOString().split('T')[0];

            if (Object.keys(query).length < 2) {
                this.createDialog({ "title": "Error", "body": 'Please enter a search option such as Employee or a date range', "buttonNo": false, "buttonYesLabel": "OK" });
                this.loading = false;
                return false;
            }

            let queryString = Object.keys(query).map(key => key + '=' + query[key]).join('&');
            // console.log(query, queryString);
            try {
                const response = await this.oasisApi.getAllPages(endpoint + '?' + queryString);
                // console.log(response);
                if (response && response.data && response.data.results) {
                    this.rowData = response.data.results;
                } else {
                    this.rowData = [];
                }
                this.$emit('changed', this.rowData);
            } catch (error) {
                console.log(error);
                this.loading = false;
                return false;
            };
            this.iteration++;
            this.loading = false;
        },

        getSelectedData() {
            if (!this.gridOptions) return [];
            return ((this.gridOptions.api || {}).getSelectedRows() || []);
        },

        getSelectedHashes() {
            if (!this.gridOptions) return [];
            return ((this.gridOptions.api || {}).getSelectedRows() || []).map((row) => row.hash);
        },

        async openAssignment(assignmentId) {
            if (!assignmentId) return false;
            window.open(`/oasis/starts/approve/${assignmentId}`, '_roster');
        },

        exportToExcel() {
            const hashes = this.gridOptions.api.getSelectedRows().map((row) => row.hash) || [];
            if (hashes.length === 0) {
                this.createDialog('Please Select at least one item to export.');
                return;
            }
            this.gridOptions.api.exportDataAsExcel();
        },

        resetSearch() {
            const now = new Date();
            const today = now.toISOString().split('T')[0];
            const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 31).toISOString().split('T')[0];
            if (this.rowData) this.rowData = undefined;
            this.searchQueryDateStart = startDate;
            this.searchQueryDateEnd = today;
        },
    },

    watch: {
        showAllShows(newValue, oldValue) {
            this.gridOptions.api.redrawRows()
        }
    },

    created() {
        this.filterOrder = [
            { field: 'entity' },
            { field: 'last_name' },
            { field: 'loanout_name' },
            { field: 'position' },
            { field: 'department' },
            { field: 'budget' },
            { field: 'union_local' },
            { field: 'status' },
        ];
        this.columnDefs = [
            {
                headerName: 'Show/Job',
                field: 'entity',
                colId: 'entity',
                checkboxSelection: true,
                headerCheckboxSelection: true,
                maxWidth: 420,
                minWidth: 100,
                editable: false,
                sortable: true,
                filter: true,
                valueFormatter: (params) => params.data.entity,
                menuTabs: [],
            },
            {
                headerName: 'Position',
                field: 'position',
                colId: 'position',
                maxWidth: 500,
                minWidth: 150,
                editable: true,
                sortable: true,
                filter: true,
                cellClass: 'editable',
                valueFormatter: (params) => params.data.job_title_override,
                menuTabs: [],
            },
            {
                headerName: 'Rate',
                field: 'rate',
                colId: 'rate',
                maxWidth: 150,
                minWidth: 70,
                editable: true,
                sortable: true,
                cellClass: 'amount editable',
                valueFormatter: (params) => params.data.rate,
                menuTabs: [],
            },
            {
                headerName: 'Rate unit',
                field: 'rate_unit',
                colId: 'rate_unit',
                maxWidth: 150,
                minWidth: 30,
                editable: true,
                sortable: true,
                cellClass: 'editable',
                valueFormatter: (params) => params.data.rate_unit,
                menuTabs: [],
            },
            {
                headerName: 'Hire Date',
                field: 'start_date',
                colId: 'start_date',
                maxWidth: 150,
                minWidth: 50,
                width: 100,
                editable: true,
                sortable: true,
                cellClass: 'editable',
                valueFormatter: (params) => {
                    if (!params.data.start_date) return null;
                    const date = new Date(params.data.start_date);
                    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).toISOString().split('T')[0];
                },
                menuTabs: [],
            },
            {
                headerName: 'Duration',
                field: 'duration',
                colId: 'duration',
                maxWidth: 100,
                minWidth: 70,
                editable: true,
                sortable: true,
                cellClass: 'editable',
                valueFormatter: (params) => params.data.duration,
                menuTabs: [],
            },
            {
                headerName: 'Estimated cost',
                field: 'cost',
                colId: 'cost',
                maxWidth: 250,
                minWidth: 100,
                editable: true,
                sortable: true,
                cellClass: 'amount editable',
                valueGetter: (params) => {
                    const duration = params.data.duration || 1;
                    let result;
                    switch (parseInt(params.data.rate_unit_id)) {
                        case 1:
                            result = (parseInt(params.data.guaranteed_hours || 0) || 8) * params.data.hourly_rate * duration;
                            break;
                        case 2:
                            result = parseFloat(params.data.rate) * duration;
                            break;
                        case 3:
                            result = parseFloat(params.data.rate) * duration / 5;
                            break;
                        default:
                            result = (parseInt(params.data.guaranteed_hours || 0) || 8) * params.data.hourly_rate * duration;
                    }

                    return result.toFixed(2);
                },
                menuTabs: [],
            },
        ]
    },

    async mounted() {
        setTimeout(() => { $('[data-toggle="tooltip"]').tooltip() }, 500);

        // Prevent back button from failing.
        history.pushState(null, document.referrer, location.href);
        window.onpopstate = function () {
            window.location.href = document.referrer;
        };

        this.resetSearch();
        this.getPlanner();
    }
});
</script>

<style>
.ag-ltr .ag-cell.amount,
.ag-ltr .ag-cell.rate {
  text-align: right;
}
.ag-ltr .ag-cell.editable {
  background-color: #3ea2ff15;
}

</style>